import { useMemo } from 'react';
import { Background, Controls, MarkerType, Position, ReactFlow, type Edge, type Node } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useTheme } from '@/components/theme/ThemeProvider';
import type { NavDetail } from '@/types/navigation-report';
import { fmtNav } from './format';

interface NavigationWebProps {
    detail: NavDetail;
    onOpenAsEntry: (path: string) => void;
}

const MAX_P2_NODES = 12;
const NODE_W = 260;
const ROW_H = 64;
const P2_X = 480;

const baseStyle = {
    width: NODE_W,
    padding: '8px 10px',
    borderRadius: 8,
    fontSize: 11,
    fontFamily: 'inherit',
    background: 'hsl(var(--card))',
    color: 'hsl(var(--foreground))',
};

function Label({ title, sub, hint }: { title: string; sub: string; hint?: string }) {
    return (
        <div className="text-left" title={hint || title}>
            <div className="truncate font-medium">{title}</div>
            <div className="text-[10px] opacity-70">{sub}</div>
        </div>
    );
}

interface GraphRow {
    id: string;
    title: string;
    sessions: number;
    share: number;
    path?: string;
    muted?: boolean;
}

const sharePct = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 1000) / 10 : 0);

/**
 * Monta os nós/arestas: P1 à esquerda; até MAX_P2_NODES P2 à direita (ordem = sessões);
 * "outras N páginas" (se passar do teto) e "não avançaram" fecham o total da P1.
 * Espessura da aresta ∝ sessões (1–8 px).
 */
export function buildGraph(detail: NavDetail): { nodes: Node[]; edges: Edge[] } {
    const top = detail.next_pages.slice(0, MAX_P2_NODES);
    const rest = detail.next_pages.slice(MAX_P2_NODES);
    const restSessions = rest.reduce((acc, p) => acc + p.sessions, 0);
    const rows: GraphRow[] = top.map((p, i) => ({ id: `p2-${i}`, title: p.path, sessions: p.sessions, share: p.share_pct, path: p.path }));
    if (rest.length > 0) {
        rows.push({ id: 'rest', title: `outras ${fmtNav.num(rest.length)} páginas`, sessions: restSessions, share: sharePct(restSessions, detail.sessions), muted: true });
    }
    rows.push({ id: 'exit', title: 'não avançaram', sessions: detail.not_advanced, share: sharePct(detail.not_advanced, detail.sessions), muted: true });

    const maxSessions = Math.max(1, ...rows.map((r) => r.sessions));
    const centerY = ((rows.length - 1) * ROW_H) / 2;

    const nodes: Node[] = [
        {
            id: 'p1',
            position: { x: 0, y: centerY },
            data: { label: <Label title={detail.path} sub={`${fmtNav.num(detail.sessions)} sessões entraram aqui`} /> },
            style: { ...baseStyle, border: '2px solid #10b981' },
            sourcePosition: Position.Right,
            targetPosition: Position.Left,
            draggable: false,
        },
        ...rows.map<Node>((r, i) => ({
            id: r.id,
            position: { x: P2_X, y: i * ROW_H },
            data: {
                label: <Label title={r.title} sub={`${fmtNav.num(r.sessions)} sessões · ${fmtNav.pct1(r.share)}`} hint={r.path ? `${r.path} — clique para abrir como entrada` : r.title} />,
                path: r.path,
            },
            style: r.muted
                ? { ...baseStyle, border: '1px dashed #64748b', opacity: 0.8 }
                : { ...baseStyle, border: '1px solid #3b82f6', cursor: 'pointer' },
            sourcePosition: Position.Right,
            targetPosition: Position.Left,
            draggable: false,
        })),
    ];

    const edges: Edge[] = rows.map((r) => ({
        id: `e-${r.id}`,
        source: 'p1',
        target: r.id,
        type: 'smoothstep',
        label: `${fmtNav.num(r.sessions)} · ${fmtNav.pct1(r.share)}`,
        labelStyle: { fontSize: 10, fill: 'hsl(var(--foreground))' },
        labelBgStyle: { fill: 'hsl(var(--card))', fillOpacity: 0.9 },
        style: { strokeWidth: 1 + 7 * (r.sessions / maxSessions), stroke: r.muted ? '#64748b' : '#3b82f6', strokeDasharray: r.muted ? '4 4' : undefined },
        markerEnd: { type: MarkerType.ArrowClosed, color: r.muted ? '#64748b' : '#3b82f6' },
    }));

    return { nodes, edges };
}

/** Teia P1 → P2 (React Flow). Clique num nó P2 abre aquela página como entrada. */
export function NavigationWeb({ detail, onOpenAsEntry }: NavigationWebProps) {
    const { theme } = useTheme();
    const { nodes, edges } = useMemo(() => buildGraph(detail), [detail]);

    return (
        <div className="rounded-lg border border-border bg-card">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
                <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                    Teia de navegação <span className="text-foreground/30">· P1 → P2, espessura = sessões</span>
                </p>
                <p className="text-[10px] text-muted-foreground">até {MAX_P2_NODES} destinos no grafo · lista completa ao lado</p>
            </div>
            <div className="h-[460px] font-mono">
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    colorMode={theme}
                    fitView
                    fitViewOptions={{ padding: 0.15, maxZoom: 1 }}
                    minZoom={0.2}
                    nodesDraggable={false}
                    nodesConnectable={false}
                    elementsSelectable={false}
                    zoomOnScroll={false}
                    panOnScroll
                    proOptions={{ hideAttribution: true }}
                    onNodeClick={(_, node) => {
                        const path = (node.data as { path?: string }).path;
                        if (path) onOpenAsEntry(path);
                    }}
                >
                    <Background gap={20} size={1} />
                    <Controls showInteractive={false} />
                </ReactFlow>
            </div>
        </div>
    );
}
